import { useEffect, useState } from "react";
import { Landmark, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { EmptyState, Field } from "@/components/common";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { NumInput } from "@/components/NumInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useApi } from "@/hooks/useApi";
import { api, brl, errMsg } from "@/lib/api";

export const FIN_CATEGORIES = ["Empréstimo", "Financiamento", "Reforma", "Juros e tarifas bancárias", "Cartão / antecipação", "Outros"];
const EMPTY = { name: "", category: "Empréstimo", monthly_amount: "", installments_total: "", installments_paid: "", notes: "" };

const FinDialog = ({ open, onOpenChange, item, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setF(item ? { ...item } : EMPTY); }, [item, open]);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const body = { name: f.name, category: f.category, monthly_amount: Number(f.monthly_amount) || 0, installments_total: Number(f.installments_total) || 0, installments_paid: Number(f.installments_paid) || 0, notes: f.notes || "" };
      item?.id ? await api.put(`/financial-costs/${item.id}`, body) : await api.post("/financial-costs", body);
      toast.success("Custo financeiro salvo"); onSaved(); onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="fin-dialog">
        <DialogHeader><DialogTitle className="font-display">{item?.id ? "Editar custo financeiro" : "Novo custo financeiro"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Descrição"><Input data-testid="fin-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Empréstimo capital de giro, Reforma da cozinha..." /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Tipo">
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                <SelectTrigger data-testid="fin-category"><SelectValue /></SelectTrigger>
                <SelectContent>{FIN_CATEGORIES.map((c) => <SelectItem key={c} value={c} data-testid={`fin-category-${c}`}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Parcela mensal (R$)"><NumInput data-testid="fin-amount" min="0" step="0.01" required className="num" value={f.monthly_amount} onChange={(e) => setF({ ...f, monthly_amount: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Total de parcelas (opcional)"><NumInput data-testid="fin-installments" min="0" step="1" className="num" value={f.installments_total} onChange={(e) => setF({ ...f, installments_total: e.target.value })} /></Field>
            <Field label="Parcelas pagas (opcional)"><NumInput data-testid="fin-paid" min="0" step="1" className="num" value={f.installments_paid} onChange={(e) => setF({ ...f, installments_paid: e.target.value })} /></Field>
          </div>
          <Field label="Observação"><Input data-testid="fin-notes" value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
          <Button data-testid="fin-save" type="submit" className="w-full" disabled={saving}>{saving ? "Salvando..." : "Salvar"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const FinancialCosts = () => {
  const { data, loading, reload } = useApi("/financial-costs");
  const [dlg, setDlg] = useState({ open: false, item: null });
  const list = data?.items || [];
  const remove = async (id) => { try { await api.delete(`/financial-costs/${id}`); toast.success("Removido"); reload(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div className="grid lg:grid-cols-12 gap-6" data-testid="financial-costs">
      <div className="lg:col-span-4 space-y-4">
        <div className="rounded-2xl border border-border bg-card p-6 rise rise-1">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Total mensal financeiro</p>
          <p className="num text-4xl font-extrabold text-primary mt-2" data-testid="fin-total">{brl(data?.total)}</p>
          <p className="text-xs text-muted-foreground mt-1">{list.length} compromisso(s) · entra no DRE após o Lucro Operacional</p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-6 space-y-3 rise rise-2">
          <p className="font-display font-semibold">Por tipo</p>
          {(data?.by_category || []).map((c) => (
            <div key={c.name} data-testid={`fin-cat-${c.name}`}>
              <div className="flex justify-between text-sm"><span>{c.name}</span><span className="num">{brl(c.value)}</span></div>
              <div className="h-1.5 rounded-full bg-secondary mt-1 overflow-hidden"><div className="h-full bg-amber-500 rounded-full" style={{ width: `${data.total ? (c.value / data.total) * 100 : 0}%` }} /></div>
            </div>
          ))}
          {!data?.by_category?.length && <p className="text-sm text-muted-foreground">Nenhum custo financeiro.</p>}
        </div>
        <Button onClick={() => setDlg({ open: true, item: null })} className="w-full" data-testid="fin-new-btn"><Plus className="w-4 h-4 mr-2" />Novo custo financeiro</Button>
      </div>
      <div className="lg:col-span-8">
        {!loading && !list.length ? (
          <EmptyState testId="fin-empty" icon={Landmark} title="Nenhum custo financeiro" text="Empréstimos, financiamentos de equipamentos, reformas parceladas e juros bancários ficam aqui, separados das despesas fixas."
            action={<Button onClick={() => setDlg({ open: true, item: null })} data-testid="fin-empty-new">Cadastrar</Button>} />
        ) : (
          <div className="rounded-2xl border border-border bg-card overflow-hidden rise rise-2">
            <Table>
              <TableHeader><TableRow><TableHead>Descrição</TableHead><TableHead>Tipo</TableHead><TableHead className="hidden sm:table-cell">Parcelas</TableHead><TableHead className="text-right">Parcela/mês</TableHead><TableHead className="w-24"></TableHead></TableRow></TableHeader>
              <TableBody>
                {list.map((x) => (
                  <TableRow key={x.id} data-testid={`fin-row-${x.id}`}>
                    <TableCell><p className="font-medium">{x.name}</p>{x.notes && <p className="text-xs text-muted-foreground">{x.notes}</p>}</TableCell>
                    <TableCell><Badge variant="secondary">{x.category}</Badge></TableCell>
                    <TableCell className="hidden sm:table-cell num text-muted-foreground">{x.installments_total ? `${x.installments_paid}/${x.installments_total}` : "—"}</TableCell>
                    <TableCell className="text-right num font-semibold">{brl(x.monthly_amount)}</TableCell>
                    <TableCell><div className="flex justify-end"><Button variant="ghost" size="icon" data-testid={`fin-edit-${x.id}`} onClick={() => setDlg({ open: true, item: x })} aria-label="Editar"><Pencil className="w-4 h-4" /></Button><ConfirmDelete what={`"${x.name}"`} onConfirm={() => remove(x.id)} testId={`fin-delete-${x.id}`} /></div></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      <FinDialog open={dlg.open} onOpenChange={(o) => setDlg({ ...dlg, open: o })} item={dlg.item} onSaved={reload} />
    </div>
  );
};
